import React, { useState, useEffect, useCallback } from 'react';
import { adminGetTenants, adminCreateTenant, adminSetTenantStatut, adminResetTenantPassword } from '../../api';
import StatusBadge from '../StatusBadge';
import ConfirmModal from '../ConfirmModal';

const FORM_VIDE = { nom: '', email: '', password: '' };

function genPassword() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
  let pwd = '';
  const rand = new Uint32Array(12);
  window.crypto.getRandomValues(rand);
  rand.forEach(n => { pwd += chars[n % chars.length]; });
  return pwd;
}

function AdminTenants() {
  const [tenants, setTenants] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState(FORM_VIDE);
  const [alert, setAlert] = useState(null);
  const [confirm, setConfirm] = useState(null);
  // Mot de passe provisoire affiché une seule fois après création/reset
  const [credentials, setCredentials] = useState(null);

  const showAlert = (message, type = 'success') => {
    setAlert({ message, type });
    setTimeout(() => setAlert(null), 5000);
  };

  const loadTenants = useCallback(async () => {
    try {
      const res = await adminGetTenants();
      setTenants(res.data);
    } catch (err) {
      showAlert(err.response?.data?.error || 'Erreur de chargement', 'error');
    }
  }, []);

  useEffect(() => { loadTenants(); }, [loadTenants]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await adminCreateTenant(formData);
      setShowModal(false);
      setCredentials({ nom: formData.nom, email: formData.email, password: formData.password, action: 'créé' });
      setFormData(FORM_VIDE);
      loadTenants();
    } catch (err) {
      showAlert(err.response?.data?.error || 'Erreur lors de la création', 'error');
    }
  };

  const handleStatut = (t) => {
    const nouveau = t.statut === 'actif' ? 'suspendu' : 'actif';
    if (nouveau !== 'suspendu') {
      adminSetTenantStatut(t.id, nouveau)
        .then(() => { showAlert('Tenant réactivé'); loadTenants(); })
        .catch(err => showAlert(err.response?.data?.error || 'Erreur', 'error'));
      return;
    }
    setConfirm({
      title: `Suspendre « ${t.nom} »`,
      message: `L'utilisateur ne pourra plus se connecter tant que son compte est suspendu.`,
      confirmLabel: 'Suspendre',
      variant: 'danger',
      onConfirm: async () => {
        setConfirm(null);
        try {
          await adminSetTenantStatut(t.id, 'suspendu');
          showAlert('Tenant suspendu');
          loadTenants();
        } catch (err) {
          showAlert(err.response?.data?.error || 'Erreur', 'error');
        }
      },
    });
  };

  const handleResetPassword = (t) => {
    setConfirm({
      title: `Réinitialiser le mot de passe`,
      message: `Un nouveau mot de passe provisoire sera généré pour « ${t.nom} ».\n\nL'utilisateur devra le changer à sa prochaine connexion.`,
      confirmLabel: 'Réinitialiser',
      variant: 'primary',
      onConfirm: async () => {
        setConfirm(null);
        const password = genPassword();
        try {
          await adminResetTenantPassword(t.id, password);
          setCredentials({ nom: t.nom, email: t.user_email, password, action: 'réinitialisé' });
          loadTenants();
        } catch (err) {
          showAlert(err.response?.data?.error || 'Erreur', 'error');
        }
      },
    });
  };

  return (
    <div>
      {alert && <div className={`alert alert-${alert.type === 'error' ? 'error' : 'success'}`}>{alert.message}</div>}

      {credentials && (
        <div className="alert alert-success credentials-box">
          <div>
            <strong>Compte {credentials.action} — transmettez ces identifiants au client (affichés une seule fois) :</strong>
            <div className="credentials-line">Email : <code>{credentials.email}</code></div>
            <div className="credentials-line">Mot de passe provisoire : <code>{credentials.password}</code></div>
          </div>
          <button className="btn btn-secondary btn-sm" onClick={() => setCredentials(null)}>Fermer</button>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2>Tenants</h2>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>+ Nouveau tenant</button>
      </div>

      <div className="card">
        <table className="table">
          <thead>
            <tr>
              <th>Entreprise</th>
              <th>Email utilisateur</th>
              <th>Statut</th>
              <th>Factures</th>
              <th>Dernière connexion</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {tenants.length === 0 ? (
              <tr><td colSpan="6" style={{ textAlign: 'center' }}>Aucun tenant</td></tr>
            ) : (
              tenants.map(t => (
                <tr key={t.id}>
                  <td>{t.nom}</td>
                  <td>{t.user_email || '—'}</td>
                  <td><StatusBadge statut={t.statut} /></td>
                  <td className="amount">{t.nb_factures}</td>
                  <td>{t.last_login_at ? new Date(t.last_login_at).toLocaleString('fr-FR') : 'Jamais'}</td>
                  <td>
                    <button className="btn btn-secondary btn-sm" onClick={() => handleStatut(t)} style={{ marginRight: '5px' }}>
                      {t.statut === 'actif' ? 'Suspendre' : 'Réactiver'}
                    </button>
                    <button className="btn btn-secondary btn-sm" onClick={() => handleResetPassword(t)}>
                      Réinitialiser mdp
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="modal" onClick={() => { setShowModal(false); setFormData(FORM_VIDE); }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Nouveau tenant</h2>
              <button className="close-btn" onClick={() => { setShowModal(false); setFormData(FORM_VIDE); }}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Nom de l'entreprise *</label>
                <input
                  type="text"
                  required
                  autoFocus
                  value={formData.nom}
                  onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                  placeholder="Dupont SARL"
                />
              </div>
              <div className="form-group">
                <label>Email de l'utilisateur *</label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="contact@dupont.fr"
                />
              </div>
              <div className="form-group">
                <label>Mot de passe provisoire *</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type="text"
                    required
                    minLength={8}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="8 caractères minimum"
                  />
                  <button type="button" className="btn btn-secondary" onClick={() => setFormData({ ...formData, password: genPassword() })}>
                    Générer
                  </button>
                </div>
              </div>
              <div className="actions">
                <button type="submit" className="btn btn-primary">Créer le compte</button>
                <button type="button" className="btn btn-secondary" onClick={() => { setShowModal(false); setFormData(FORM_VIDE); }}>
                  Annuler
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmModal
        open={!!confirm}
        title={confirm?.title}
        message={confirm?.message}
        confirmLabel={confirm?.confirmLabel}
        variant={confirm?.variant || 'primary'}
        onConfirm={confirm?.onConfirm}
        onCancel={() => setConfirm(null)}
      />
    </div>
  );
}

export default AdminTenants;
